/**
 * API response types — mirror the Zod schemas in sc-platform. Kept in
 * sync manually for now; future: generate from sc-platform's
 * `/docs/json` OpenAPI spec.
 */

export type UserRole = 'SUPER_ADMIN' | 'TENANT_ADMIN' | 'MEMBER';
export type UserStatus = 'PENDING' | 'ACTIVE' | 'DEACTIVATED';

export interface User {
  id: string;
  tenantId: string | null;
  email: string;
  name: string | null;
  /** Admin roles held — present on /api/me ONLY (they gate portal surfaces);
   *  the users list stopped reporting them. No account tier — an empty list
   *  administers nothing. */
  roles?: string[];
  /**
   * Effective scopes = group-inherited scopes — groups are the single source ("what you can
   * actually do"). Present on /api/me. Gate capabilities (e.g. agent access) on
   * THIS, never on `scopes` (direct only) — otherwise group-granted access is
   * wrongly hidden.
   */
  effectiveScopes?: string[];
  /**
   * Admin surfaces this user may operate (e.g. 'knowledge.view'). A DIFFERENT
   * axis from scopes: these come from assigned admin roles and are filtered by
   * the tenant's module entitlement. Present on /api/me only. Gate ADMIN PAGES
   * on this; gate data/agent capabilities on effectiveScopes.
   */
  permissions?: string[];
  /** Groups the user belongs to — powers the users table's group column + filter. */
  groups?: { id: string; name: string }[];
  status: UserStatus;
  createdAt: string;
  activatedAt: string | null;
  lastLoginAt: string | null;
}

export interface Tenant {
  id: string;
  slug: string;
  name: string;
  createdAt: string;
}

export interface MeResponse {
  user: User;
  tenant: Tenant;
}

/**
 * POST /api/agent (orchestrator) request. `conversationId` omitted starts a
 * fresh server-side thread; the response returns the id to replay on the next turn.
 */
export interface AgentRequest {
  message: string;
  conversationId?: string;
  /** Bind a new conversation to an alert case (deep link from a Teams card). */
  caseRef?: string;
  /** Answers the bot's question: a tapped option's `value`, or no `value` when typed in the card. */
  questionAnswer?: { questionId: string; value?: string };
}

/** GET /api/agent/threads — the caller's past conversations, for the sidebar. */
export interface AgentThreadSummary {
  id: string;
  title: string;
  updatedAt: string;
}

export interface AgentThreadListResponse {
  threads: AgentThreadSummary[];
}

/** A bot the caller has (GET /api/me/bots). */
export interface MyBot {
  id: string;
  name: string;
  description: string;
  /** They turned it off: the thread stays readable, nothing new arrives, no replies. */
  turnedOff: boolean;
}

export interface MyBotsResponse {
  bots: MyBot[];
}

/** One of the caller's existing bot threads (GET /api/agent/bot-threads). */
export interface BotThread {
  botId: string;
  conversationId: string;
  /** Messages the bot sent on its own since the caller last read the thread. */
  unread: number;
  /** The newest unread message's headline, or null when nothing is unread. */
  latestHeadline: string | null;
}

/** GET /api/agent/threads/:threadId/messages — one conversation, replayed. */
export interface AgentThreadMessage {
  /**
   * The message's stable server id — present on every replayed message. Feedback
   * correlates to the assistant message by this id, so the 👍/👎 control renders
   * on every replayed turn and survives a reload (no fragile turn-stamping).
   */
  id: string;
  role: 'user' | 'assistant';
  content: string;
  /** Tools this assistant turn ran, each at its offset in `content` — lets a
   * reloaded turn show the same inline markers the live stream did. */
  toolMarkers?: { label: string; detail?: string | null; afterChar: number }[];
  /** Set on a message a bot sent on its own (a delivered letter), not a reply. */
  bot?: BotMessageInfo;
  /** The question that bot message asked, as it stands now. */
  question?: BotQuestion;
}

/** A bot's question, shaped for the design system's `Chat.Choices`. */
export interface BotQuestion {
  id: string;
  question: string;
  options: { value: string; label: string; description?: string }[];
  allowOther: true;
  status: 'open' | 'answered' | 'expired' | 'replaced';
  answered?: { kind: 'option'; value: string } | { kind: 'text'; text: string };
  /** Who answered, when someone else asked it did. */
  answeredBy?: string;
}

/** What a bot-sent message carries for its header and footer. */
export interface BotMessageInfo {
  headline: string;
  /** The bot marked it urgent: they should act now. */
  urgent?: true;
  /** Why this person got it ("Named in the event (Mechanic email)"). */
  reason: string;
  /** The workflow that sent it. */
  duty?: string;
  sentAt: string;
}

/** One page of a thread, oldest first. */
export interface AgentThreadMessagesResponse {
  messages: AgentThreadMessage[];
  /** Pass as `before` for the next older page; null at the thread's start. */
  nextBefore: string | null;
}

export const USAGE_PERIODS = ['24h', '7d', '30d', '60d', '90d'] as const;
export type UsagePeriodPreset = (typeof USAGE_PERIODS)[number];

export interface ModelPricing {
  model: string;
  inputPerMTokUsd: number;
  outputPerMTokUsd: number;
  cacheReadPerMTokUsd: number;
  cacheWritePerMTokUsd: number;
}

/** Cost per chat answer and the share of chat input read from cache; null without chat. */
interface AnswerStats {
  costPerAnswerUsd: number | null;
  cacheShare: number | null;
}

export interface UsageTotals extends AnswerStats {
  turns: number;
  activeUsers: number;
  inputTokens: number;
  outputTokens: number;
  totalTokens: number;
  cachedInputTokens: number;
  estimatedCostUsd: number;
}

export interface UsageTrendRow extends AnswerStats {
  /** "YYYY-MM-DD", or "YYYY-MM-DDTHH:00" in `byHour`, in the tenant's zone. */
  date: string;
  turns: number;
  totalTokens: number;
  activeUsers: number;
}

export interface UsageUserRow {
  userId: string | null;
  label: string;
  turns: number;
  totalTokens: number;
  estimatedCostUsd: number;
  /** Chat spend per chat turn; their letters and side jobs are not in it. */
  costPerTurnUsd: number | null;
  /** Their spend outside chat (bot letters, watch deliveries, titles); absent from older APIs. */
  otherCostUsd?: number;
  /** Share of chat input read from cache, 0 to 1; null without chat input. */
  fromCacheShare: number | null;
  /** The tenant's last 7 days, oldest first. */
  last7DaysUsd: number[];
}

/** One kind of work and what it cost. The last three fields are absent from older APIs. */
export interface UsageSourceRow {
  source: string;
  calls: number;
  costUsd: number;
  /** What one of `calls` is: a chat turn, a bot letter, a watch delivery, a workflow step. */
  unit?: 'turn' | 'letter' | 'delivery' | 'step' | 'call';
  costPerUnitUsd?: number | null;
  fromCacheShare?: number | null;
}

/** `GET /api/usage` — usage and estimated spend for a period. */
export interface UsageSummaryResponse {
  period: UsagePeriodPreset | 'custom';
  windowDays: number;
  since: string;
  until: string;
  totals: UsageTotals;
  previous: { since: string; until: string; totals: UsageTotals };
  byTool: { toolName: string; count: number; avgResultTokens: number | null }[];
  byUser: UsageUserRow[];
  byDay: UsageTrendRow[];
  byHour: UsageTrendRow[];
  spend: {
    bySource: UsageSourceRow[];
    byModel: { model: string; calls: number; costUsd: number; pricing: ModelPricing | null }[];
    bySourcePerDay: { date: string; source: string; costUsd: number }[];
    makeup: {
      freshInputUsd: number;
      cacheReadUsd: number;
      cacheWriteUsd: number;
      outputUsd: number;
      tokens: { freshInput: number; cacheRead: number; cacheWrite: number; output: number };
    };
    unpricedCalls: number;
  };
  month: {
    /** "YYYY-MM". */
    month: string;
    dayOfMonth: number;
    daysInMonth: number;
    spentUsd: number;
    projectedUsd: number;
    previousMonthUsd: number;
    previousMonthToDateUsd: number;
  };
  pricing: ModelPricing;
}

export interface UsageConversationRow {
  conversationId: string;
  title: string | null;
  userId: string | null;
  userLabel: string;
  turns: number;
  costUsd: number;
  costPerTurnUsd: number | null;
  /** Share of chat input read from cache, 0 to 1; null without chat input. */
  fromCacheShare: number | null;
  lastActiveAt: string;
}

/** `GET /api/usage/conversations` — the period's most expensive conversations. */
export interface UsageConversationsResponse {
  since: string;
  until: string;
  conversations: UsageConversationRow[];
}

export interface CostParts {
  freshInputUsd: number;
  cacheReadUsd: number;
  cacheWriteUsd: number;
  outputUsd: number;
}

/** `GET /api/usage/conversations/:id` — one conversation's cost, turn by turn. No message text. */
export interface UsageConversationDetailResponse {
  conversationId: string;
  title: string | null;
  userId: string | null;
  userLabel: string;
  startedAt: string;
  lastActiveAt: string;
  models: string[];
  turns: number;
  costUsd: number;
  costPerTurnUsd: number | null;
  /** Share of chat input read from cache, 0 to 1; null without chat input. */
  fromCacheShare: number | null;
  tenantCostPerTurnUsd: number | null;
  summaries: { at: string; costUsd: number }[];
  /** Newest first. */
  turnRows: {
    turnId: string;
    at: string;
    model: string | null;
    tools: { toolName: string; count: number }[];
    steps: number | null;
    inputTokens: number;
    cacheReadTokens: number;
    cacheWriteTokens: number;
    outputTokens: number;
    costUsd: number;
    costParts: CostParts | null;
  }[];
  truncated: boolean;
}

/** One turn the caller has already rated (GET /api/feedback/mine), for rehydration. */
export interface MessageFeedback {
  /** The rated assistant message's id (the feedback correlation). */
  messageId: string;
  rating: 'up' | 'down';
}

export interface ConversationFeedbackResponse {
  feedback: MessageFeedback[];
}

/**
 * Integration identity. NOT a closed union — the server's PROVIDER_CATALOG
 * defines which keys exist at runtime (admin portal reads them via
 * `GET /api/integrations`). Kept as `string` here so client code stays
 * future-proof when a new integration (NetSuite, etc.) is added on the
 * server with no client code change.
 */
export type IntegrationKey = string;

/**
 * Field-kind taxonomy mirrors the server's `fieldKindSchema`:
 *  - secret: credential, masked in the form (server stores in Key Vault)
 *  - param: vendor-side identifier, shown plainly (server stores inline)
 *  - config: configuration value e.g. base URL (server stores inline)
 * The form uses `kind` to decide input type + masking — admins never see
 * the kind itself as UI text. Help text comes from `description`.
 */
export type IntegrationFieldKind = 'secret' | 'param' | 'config';

/**
 * Input type for the form field. Defaults to 'text' based on field kind
 * (password for secrets, text for others). Can be overridden to 'select',
 * 'checkbox', etc. When 'select' or 'checkbox', `options` must be present.
 */
export type IntegrationFieldInputType = 'text' | 'password' | 'select' | 'checkbox' | 'radio';

export interface IntegrationFieldOption {
  label: string;
  value: string;
  /** One line under the label when the choice renders as cards (inputType 'radio'). */
  description?: string;
}

export interface IntegrationField {
  name: string;
  label: string;
  description: string;
  kind: IntegrationFieldKind;
  defaultValue?: string;
  /** Input type override. Defaults based on kind (password for secrets, text for others). */
  inputType?: IntegrationFieldInputType;
  /** Options for select/checkbox/radio inputs. Required if inputType is 'select'|'checkbox'|'radio'. */
  options?: IntegrationFieldOption[];
  /** Name of another field this field depends on for visibility. */
  dependsOn?: string;
  /** Value of the dependent field that makes this field visible. */
  visibleWhen?: string;
}

/**
 * Data category an integration fills — primary grouping axis in the catalog UI.
 * Mirror of the server's integrationCategorySchema. Kept as a string union
 * that stays open in practice — the catalog is server-driven, so new
 * categories appear without a client change.
 */
export type IntegrationCategory =
  | 'telemetry'
  | 'work_history'
  | 'assets'
  | 'knowledge_base'
  | 'schedule'
  | 'directory'
  | 'regions'
  | 'fleet_location'
  | 'inventory'
  // A shared account/credential bundle (e.g. a NetSuite account) that one or
  // more domain integrations reuse. Rendered as the connection "parent" with
  // its capabilities nested under it, not as a standalone data integration.
  | 'connection';

/**
 * Upstream vendor system. Mirror of the server's integrationSystemSchema.
 */
export type IntegrationSystem =
  | 'mlink'
  | 'enbase'
  | 'netsuite'
  | 'azure_ai_search'
  // Service Compression's own hosted infrastructure — nothing for the tenant
  // to configure, so its cards carry our mark rather than a vendor monogram.
  | 'sc_platform'
  | 'samsara'
  // A generic HTTP endpoint conforming to the platform Data Contract
  // (a tenant's conformance layer — e.g. the SC Data Hub's Contract API).
  | 'custom'
  // A tenant-hosted MCP server; the `custom_mcp` connection holds its URL + token.
  | 'mcp';

export interface IntegrationDefinition {
  key: IntegrationKey;
  displayName: string;
  description: string;
  /** Data category this integration fills — primary grouping axis in the catalog UI. */
  category: IntegrationCategory;
  /** Vendor system — shown as a badge. */
  system: IntegrationSystem;
  fields: IntegrationField[];
}

export interface IntegrationCatalogResponse {
  integrations: IntegrationDefinition[];
}

export interface TenantIntegrationSummary {
  isConnected?: boolean;
  /**
   * The row's id — how a configured integration is addressed for update, test
   * and delete. A key names a TYPE and can have several instances, so it can't
   * identify one row.
   */
  id: string;
  key: IntegrationKey;
  /** Display label. Empty means "use the catalog's displayName". */
  name: string;
  /** Stable instance id, unique per (tenant, key). Distinguishes two instances. */
  slug: string;
  /** The connection this integration draws credentials from, if any. */
  connectionId: string | null;
  configuredFields: string[];
  /**
   * Stored values for NON-SECRET fields (kind: 'param' | 'config'). The
   * server filters secret-kind values out before responding. Used by the
   * Edit form to prefill inputs the admin would otherwise have to retype.
   */
  config: Record<string, string>;
  lastTestedAt: string | null;
  lastTestedOk: boolean | null;
  updatedAt: string;
  /**
   * Custom MCP connections only. `mcpTools` is what the server advertised at
   * the last passing test (null until then); `mcpApprovedTools` the names the
   * assistant may use; `mcpAllowedGroupIds` whose members may use them. Empty
   * on every other kind of row.
   */
  mcpTools: McpToolDescriptor[] | null;
  mcpToolsFetchedAt: string | null;
  mcpApprovedTools: string[];
  mcpAllowedGroupIds: string[];
  /** How a Custom MCP connection authenticates; `shared_token` on every other kind of row. */
  mcpAuthMode: McpAuthMode;
  /** Sign-in connections only: what Test found about the login system and the client we hold. */
  mcpOAuth: McpOAuthView | null;
}

export type McpAuthMode = 'shared_token' | 'user_oauth';

/** The admin-facing view of a sign-in connection's OAuth setup; never carries a secret. */
export interface McpOAuthView {
  resource: string | null;
  issuer: string;
  authorizationEndpoint: string | null;
  tokenEndpoint: string | null;
  registrationEndpoint: string | null;
  scopesSupported: string[];
  discoveredAt: string;
  clientId: string | null;
  registeredVia: 'dcr' | 'manual' | null;
  /** True until a client exists with the login system. */
  needsClient: boolean;
  /** Where the login system must send people back; null when the platform has none set. */
  redirectUri: string | null;
}

/** One person signed in to a sign-in connection, as an admin sees it. */
export interface McpSignInUser {
  userId: string;
  name: string | null;
  email: string;
  signedInAt: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
}

export type McpMyConnectionStatus = 'not_ready' | 'not_connected' | 'connected' | 'needs_sign_in';

/** A sign-in connection as the signed-in person sees it. */
export interface McpMyConnection {
  integrationId: string;
  name: string;
  slug: string;
  status: McpMyConnectionStatus;
  signedInAt: string | null;
  issuer: string | null;
}

export interface McpSignInStartResponse {
  status: 'redirect' | 'connected';
  authorizeUrl: string | null;
}

/** One tool a Custom MCP server advertises, as the server described it. */
export interface McpToolDescriptor {
  name: string;
  title?: string;
  description?: string;
  inputSchema: Record<string, unknown>;
  annotations?: {
    title?: string;
    readOnlyHint?: boolean;
    destructiveHint?: boolean;
    idempotentHint?: boolean;
    openWorldHint?: boolean;
  };
}

export interface TenantIntegrationListResponse {
  integrations: TenantIntegrationSummary[];
}

/**
 * One candidate source for a capability + its per-tenant state. Mirror of the
 * server's `capabilitySourceStateSchema`. `configured` = a TenantIntegration row
 * exists; `active` = it currently serves the capability (isActive).
 */
export interface CapabilitySourceState {
  key: IntegrationKey;
  configured: boolean;
  active: boolean;
  /** The binding row's id, when one exists. */
  integrationId: string | null;
  /** Which connection instance it currently reads from. */
  connectionId: string | null;
  /**
   * Which KIND of connection this source needs. Filter the tenant's integrations
   * to this key for the picker's options. Null for self-contained sources, which
   * hold their own credentials — no picker.
   */
  connectionKey: IntegrationKey | null;
}

/** A capability and the sources that can serve it. */
export interface CapabilitySources {
  capability: IntegrationCategory;
  sources: CapabilitySourceState[];
  /** Whether several sources may serve at once. False ⇒ activating is a swap. */
  supportsMultipleSources: boolean;
  /**
   * Set when the tenant already has several active sources on a capability that
   * reads one. Pre-existing config the server reports rather than silently
   * fixing, so an admin resolves it deliberately.
   */
  multiSourceConflict?: boolean;
}

export interface CapabilitySourcesListResponse {
  capabilities: CapabilitySources[];
}

/**
 * Activate a source for a capability. `replace` (default) makes it the only
 * active source (a swap); `add` joins the active set (multi-source fan-out).
 * Mirror of the server's `activateSourceBodySchema`.
 */
export interface ActivateSourceBody {
  sourceKey: IntegrationKey;
  mode: 'replace' | 'add';
  /**
   * Which connection serves this capability. Required when the tenant has more
   * than one candidate — the server refuses to guess, because binding to the
   * wrong one returns nothing while looking healthy.
   */
  connectionId?: string;
}

/**
 * Structured failure category on a test result. Lets the UI render a
 * friendly message instead of dumping raw vendor errors. Mirror of
 * `tenantIntegrationTestErrorCodeSchema` on the server.
 */
export type TenantIntegrationTestErrorCode =
  | 'invalid_credentials'
  | 'vendor_unreachable'
  | 'rate_limited'
  | 'unknown';

export interface TenantIntegrationTestResult {
  ok: boolean;
  /** Structured failure category; null on success. */
  code: TenantIntegrationTestErrorCode | null;
  /** Raw vendor error message; null on success. Kept for support / debug. */
  error: string | null;
  testedAt: string;
}

export interface UserListResponse {
  users: User[];
}

export interface ApiKey {
  id: string;
  displayName: string;
  scopes: string[];
  /** Endpoints the key is limited to ("GET /api/assets"); empty means no limit. */
  allowedEndpoints: string[];
  isEnabled: boolean;
  createdAt: string;
  lastUsedAt: string | null;
  createdBy: string | null;
}

/** An endpoint an API key can be limited to, with the scope that guards it. */
export interface AppEndpoint {
  endpoint: string;
  scope: string;
  summary: string | null;
}

export interface AppEndpointListResponse {
  endpoints: AppEndpoint[];
}

export interface ApiKeyCreatedResponse extends ApiKey {
  token: string;
}

export interface ApiKeyListResponse {
  apiKeys: ApiKey[];
}

export interface Scope {
  id: string;
  key: string;
  displayName: string;
  description: string;
  /**
   * What the scope governs: 'access' (a route, e.g. Agent.Run), 'domain' (an
   * agent, e.g. Mechanic.Use), or 'data' (a tool/read, e.g. Telemetry.Read).
   * Optional: served by newer APIs; when absent the UI infers it from the key
   * (see lib/scopeKinds.ts). Typed loosely as string so an unknown future kind
   * round-trips without breaking the build.
   */
  kind?: string;
  /** Who can HOLD it: 'user' | 'app' | 'both'. Grant pickers filter on this.
   * Optional for older servers; absent = 'both' (shown everywhere). */
  audience?: string;
  /** Picker section (unit_data, knowledge, …). Absent = 'platform'. */
  family?: string;
  createdAt: string;
}

export interface ScopesListResponse {
  scopes: Scope[];
}

/** A capability group — a named bundle of scopes that applies to its members. */
export interface Group {
  id: string;
  name: string;
  scopes: string[];
  memberCount: number;
  createdAt: string;
}

export interface GroupMember {
  userId: string;
  email: string;
  name: string | null;
  addedAt: string;
  addedBy: string | null;
}

/** A single group with its scopes + members (the detail/get response). */
export interface GroupDetail {
  id: string;
  name: string;
  scopes: string[];
  members: GroupMember[];
  createdAt: string;
}

export interface GroupListResponse {
  groups: Group[];
}

/**
 * Notification transport. TEAMS = Teams Workflows incoming webhook (carries a
 * write-only credential); TEAMS_DM = a person-addressed DM via the platform bot
 * (carries a targetUserId, no secret).
 */
export type NotificationTransport = 'TEAMS' | 'TEAMS_DM';

/**
 * A notification destination (channel). The webhook credential is write-only
 * and never returned; `targetUserId` is the DM recipient for TEAMS_DM
 * channels and null for webhook channels. What gets sent here is decided by
 * workflows — there is no alert-type routing.
 */
export interface NotificationChannel {
  id: string;
  transport: NotificationTransport;
  label: string;
  isEnabled: boolean;
  createdAt: string;
  targetUserId: string | null;
  targetGroupId: string | null;
}

export interface NotificationChannelListResponse {
  channels: NotificationChannel[];
}

/** POST …/channels/:id/test — sends a real test card. `delivered` is the outcome. */
export interface NotificationTestResponse {
  delivered: boolean;
  channel: NotificationChannel;
}

/**
 * An assistant policy document ("guardrail") — markdown the orchestrator injects
 * into every turn as a hard constraint while `isActive`. `document` is the
 * enforced text; `hasSourceText` only reports whether the original upload was
 * kept alongside it (the source itself is never returned).
 */
export interface AssistantPolicy {
  id: string;
  name: string;
  document: string;
  hasSourceText: boolean;
  contentHash: string;
  isActive: boolean;
  updatedByUserId: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * The tenant's policies plus the aggregate active-character budget. The budget
 * numbers are deliberately NOT surfaced as UI — the cap reaches the admin only
 * as the API's 400 message when a save would overflow it.
 */
export interface AssistantPolicyListResponse {
  policies: AssistantPolicy[];
  activeTotalChars: number;
  maxActiveTotalChars: number;
}

export interface AssistantPolicyCreateBody {
  name: string;
  document: string;
  sourceText?: string;
}

export interface AssistantPolicyUpdateBody {
  name?: string;
  document?: string;
  sourceText?: string;
}

/**
 * POST /api/agent/policy-format (orchestrator) — turns an arbitrary uploaded
 * document into a constraint catalog. Exactly one of `text` / `pdfBase64`.
 */
export interface PolicyFormatRequest {
  filename: string;
  text?: string;
  pdfBase64?: string;
}

/** The formatted draft. `leftOut` names input sections that weren't rules. */
export interface PolicyFormatResponse {
  document: string;
  rulesCount: number;
  leftOut: string[];
}

/** GET/PUT /api/assistant-profile — the Assistant page. */
/** An assistant bot, as the admin Bots page sees it. */
export interface Bot {
  id: string;
  name: string;
  description: string;
  instructions: string;
  enabled: boolean;
  groupIds: string[];
  /** Members of the groups it's given to, each counted once. */
  people: number;
  /** People who turned it off for themselves. */
  turnedOff: number;
  createdAt: string;
  updatedAt: string;
}

export interface BotListResponse {
  bots: Bot[];
}

export interface AssistantProfile {
  assistantName: string | null;
  orgDescription: string | null;
  timezone: string | null;
  maxAssistantNameChars: number;
  maxOrgDescriptionChars: number;
}

/** Null clears a field back to the platform fallback. */
export interface AssistantProfileUpdateBody {
  assistantName: string | null;
  orgDescription: string | null;
  timezone: string | null;
}

/**
 * ── Pipeline events (mirrors sc-platform GET /api/events — flat, uniform) ──
 */
export type PipelineEventStatus = 'pending' | 'running' | 'done' | 'failed' | 'handed_off';

export interface PipelineEvent {
  id: string;
  type: string;
  title: string;
  status: PipelineEventStatus;
  createdAt: string;
  processedAt: string | null;
}

export interface PipelineEventStats {
  open: number;
  suspended: number;
  failed: number;
  done24h: number;
}

export interface PipelineEventListResponse {
  events: PipelineEvent[];
  stats: PipelineEventStats;
  truncated: boolean;
}

export type EventsView = 'open' | 'failed' | 'all';

/**
 * ── Event catalog (mirrors sc-platform /api/events/catalog) ────────────────
 *
 * The tenant's allowlist. `held` is not a separate collection — it is a status
 * on the same row, which is why approving keeps the observed shape and counts.
 */
export type EventTypeStatus = 'approved' | 'held' | 'blocked';

export type ShapeFieldType = 'string' | 'number' | 'boolean' | 'date' | 'array' | 'object' | 'null';

/** One field of the OBSERVED shape — what senders actually send. */
export interface ShapeField {
  name: string;
  type: ShapeFieldType;
  /** Payloads carrying this field, of `seenTotal` — shown so a person can
   *  judge whether the misses were an anomaly. */
  seenIn: number;
  /** Inference's suggestion, not a rule: present in every payload so far. */
  required: boolean;
}

export interface InferredShape {
  fields: ShapeField[];
  seenTotal: number;
}

/**
 * One field an admin CONFIRMED. `required` carries no ingest meaning — a
 * payload missing it still routes, it just arrives without the key. It tells
 * a workflow author what they can count on.
 */
export interface DeclaredField {
  label?: string;
  description?: string;
  values?: string[];
  name: string;
  type: ShapeFieldType;
  required: boolean;
}

export interface DeclaredShape {
  fields: DeclaredField[];
}

export interface TenantEventType {
  id: string;
  type: string;
  status: EventTypeStatus;
  description: string;
  scopes: string[];
  /** What senders actually send. Grows on its own; never edited. */
  shape: InferredShape | null;
  /** What an admin confirmed, and the allowlist workflows are narrowed to.
   *  Null = nothing declared yet, the only case where every field passes. */
  declaredFields: DeclaredShape | null;
  /** Observed but not declared — "this sender is also sending `bug`". */
  undeclaredFields: string[];
  lastPayload: Record<string, unknown> | null;
  /** The API key that last sent it, so a held type says who to go talk to. */
  lastSenderName: string | null;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
  receivedCount: number;
  received24h: number;
  /** Enabled workflows triggered by this type. */
  workflowCount: number;
  /** Events stored for this type and replayable on approval. */
  heldCount: number;
  oldestHeldAt: string | null;
}

export interface RegisterEventTypeBody {
  acknowledgeRemovedFields?: string[];
  type: string;
  description: string;
  scopes: string[];
  /** The confirmed field list. Absent = accept what observation suggests. */
  fields?: DeclaredField[];
  /** A doorbell event: the fact it happened is the whole signal, and there is
   *  no payload. The only way to register with nothing declared. */
  signalOnly?: boolean;
  /** One example payload for a type nothing has sent yet — the server infers
   *  the shape from it. */
  examplePayload?: Record<string, unknown>;
  /** Release what arrived while the type was held, so the sweep drains it. */
  processHeld?: boolean;
}

/** What the test-event endpoint echoes back — the payload it actually sent. */
export interface TestEventResult {
  type: string;
  payload: Record<string, unknown>;
}

export type EventsTab = 'activity' | 'catalog' | 'review' | 'blocked';

/** The raw record — identical shape for every event type. */
export interface PipelineEventDetail {
  id: string;
  type: string;
  status: PipelineEventStatus;
  payload: Record<string, unknown>;
  result: unknown;
  attempts: number;
  lastError: string | null;
  createdAt: string;
  claimedAt: string | null;
  processedAt: string | null;
}

/** ── Workflows admin (GET /api/tenants/:id/workflows) ── */
export interface WorkflowStep {
  id: string;
  sentence: string;
  sub?: string;
  configurable?: string;
}

export interface WorkflowBranch {
  label: string;
  steps: WorkflowStep[];
}

export interface WorkflowRouteState {
  id: string;
  isEnabled: boolean;
  eventsLast7d: number;
  config: unknown;
}

export interface Workflow {
  key: string;
  name: string;
  description: string;
  trigger: string;
  hasConfig: boolean;
  steps: WorkflowStep[];
  branches: WorkflowBranch[];
  route: WorkflowRouteState | null;
}

export interface ApproverRef {
  kind: 'user' | 'group';
  id: string;
}

export interface ApproverCandidate extends ApproverRef {
  label: string;
  eligible: boolean;
  reason: string | null;
}

/** A composed workflow row in the overview (additive since the rollover). */
export interface ComposedWorkflowResponse {
  id: string;
  name: string;
  trigger: string;
  /** Set on a scheduled workflow; null on an event-triggered one. */
  schedule?: BuilderSchedule | null;
  enabled: boolean;
  version: number;
  definition: unknown;
  eventsLast7d: number;
  lastRun: { status: string; at: string } | null;
  issues: string[];
  /** Who a personal workflow (a watch, a report schedule) belongs to; null for the tenant's. */
  owner: { id: string; name: string } | null;
  /** When it turns itself off, if ever. */
  endsAt: string | null;
  /** Why a watch stopped working and was paused; null while it works. */
  brokenReason: string | null;
}

/** A workflow persona (core WORKFLOW_PERSONA_CATALOG) + whether the CALLER
 * could enable a workflow using it — the delegation rule, precomputed
 * server-side so the picker can explain itself. */
export interface WorkflowPersona {
  id: string;
  label: string;
  description: string;
  scopes: string[];
  enableable: boolean;
  /** What this persona always answers with. Declared by the persona, never by
   * the author — an author can't predict what an agent will find, so the
   * builder renders these read-only instead of asking them to invent a
   * schema. Same contract a tool has. */
  outputs: {
    name: string;
    kind: 'string' | 'enum' | 'boolean';
    values?: string[];
    label: string;
    longForm?: boolean;
  }[];
}

/** A routable platform trigger — buildable before the first event fires. */
export interface WorkflowTrigger {
  type: string;
  title: string;
  hint: string;
  fields: {
    name: string;
    label?: string;
    description?: string;
    kind: 'string' | 'number' | 'boolean' | 'enum' | 'other';
    values?: string[];
  }[];
  eventsLast7d: number;
  /** Which list it belongs to in the picker. */
  source: 'platform' | 'tenant';
  /** Scopes an author must ALL hold. Empty = anyone may. Already filtered
   *  server-side; carried so the picker can explain a type it DOES show. */
  scopes: string[];
}

/** Step metadata supplied by the workflow API; editor forms remain local. */
export interface WorkflowStepMetadata {
  type: BuilderStep['type'];
  label: string;
  hint: string;
  group: string;
  fork: boolean;
}

export interface WorkflowsOverviewResponse {
  stepTypes: WorkflowStepMetadata[];
  workflows: Workflow[];
  composed: ComposedWorkflowResponse[];
  unmatchedLast7d: number;
  /** Runs parked on a person, and runs that gave up, in the same window. */
  runsWaiting: number;
  runsFailed: number;
  approverCandidates: ApproverCandidate[];
  personas: WorkflowPersona[];
  triggers: WorkflowTrigger[];
}

/** The escalation route's config shape (validated server-side). */
export interface HitlRouteConfig {
  hitl?: { enabled: boolean; approvers: ApproverRef[] };
}

/** ── Webhooks (machine event distribution — GET/POST /api/tenants/:id/webhooks) ── */
export interface Webhook {
  id: string;
  label: string;
  url: string;
  isEnabled: boolean;
  createdAt: string;
}
export interface WebhookListResponse {
  webhooks: Webhook[];
}
/** Returned once at create/rotate — never retrievable again. */
export interface WebhookWithSecret extends Webhook {
  secret: string;
}

/** One feedback item as the review queue sees it (GET /api/feedback). */
export type FeedbackStatus = 'open' | 'resolved' | 'noise' | 'praise';

/** Item count per status tab. */
export type FeedbackCounts = Record<FeedbackStatus, number>;

export interface FeedbackItem {
  id: string;
  conversationId: string;
  messageId: string;
  rating: 'up' | 'down';
  comment: string | null;
  categories: string[];
  status: FeedbackStatus;
  triageCategory:
    | 'knowledge_base'
    | 'model_behavior'
    | 'code_bug'
    | 'data_gap'
    | 'praise'
    | 'noise'
    | null;
  triageSeverity: 'low' | 'medium' | 'high' | null;
  triageSummary: string | null;
  triageKbSources: string[];
  triagedAt: string | null;
  resolutionNote: string | null;
  githubUrl: string | null;
  closedAt: string | null;
  closedByName: string | null;
  duplicateOfFeedbackId: string | null;
  question: string | null;
  userId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface FeedbackListResponse {
  feedback: FeedbackItem[];
  nextCursor: string | null;
}

/** A transcript message from the orchestration review route. */
export interface ReviewTranscriptMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  retrieval?: {
    subjectId: string | null;
    subjectName: string | null;
    sourceId: string | null;
    title: string | null;
  }[];
}

/** `messageId` echoes the isolated rated turn; null = whole-thread fallback. */
export interface ReviewTranscriptResponse {
  messages: ReviewTranscriptMessage[];
  messageId: string | null;
}

// ── Workflow builder ────────────────────────────────────────────────────────
// Wire shapes mirror @sc/core workflows/{definition,schemas}.ts exactly.

/** `changed`: differs from the same value in the workflow's previous run (watches only). */
export type BuilderConditionOp = 'eq' | 'neq' | 'contains' | 'gt' | 'lt' | 'exists' | 'changed';

export interface BuilderCondition {
  path: string;
  op: BuilderConditionOp;
  value?: string | number | boolean;
}

export interface BuilderConditionGroup {
  match: 'all' | 'any';
  conditions: BuilderCondition[];
}

export interface BuilderApproverRef {
  kind: 'user' | 'group';
  id: string;
}

export interface BuilderBranchCase {
  label: string;
  when: BuilderConditionGroup;
  steps: BuilderStep[];
}

/** When a scheduled workflow runs: weekdays or days of the month, plus a time in
 *  the tenant's timezone. The server stores it on the workflow, not in the step definition. */
export type BuilderSchedule =
  | {
      /** 0 = Sunday. */
      days: number[];
      /** "HH:MM", 24-hour. */
      time: string;
    }
  | {
      /** 1-31; a day the month lacks runs on its last day. */
      monthDays: number[];
      time: string;
    }
  | {
      /** Once, on this date ("YYYY-MM-DD"), in the tenant's timezone. */
      on: string;
      time: string;
    }
  | {
      /** Every this many minutes from `from`: a person's frequent watch, made in chat. */
      everyMinutes: number;
      from: string;
    };

/** The literal a caller sends to create a scheduled workflow. The real trigger
 *  key embeds the workflow's own id and is minted server-side, because that id
 *  does not exist until the row does. */
export const SCHEDULE_TRIGGER = 'schedule';

export const isScheduleTrigger = (trigger: string): boolean =>
  trigger === SCHEDULE_TRIGGER || trigger.startsWith('schedule:');

/** When a message step may reach a person. */
export interface BuilderDeliverWindow {
  /** 0 = Sunday. */
  days: number[];
  /** "HH:MM", 24-hour, in the tenant's timezone. */
  start: string;
  end: string;
}

export type BuilderStep =
  | { type: 'filter'; name?: string; match: 'all' | 'any'; conditions: BuilderCondition[] }
  | {
      type: 'notify';
      name?: string;
      channelIds: string[];
      recipients?: { kind: 'user'; id: string }[];
      title: string;
      body: string;
      /** Open a case for the event and put its ref + chat link on the card.
       * Absent on older definitions — the server defaults it to true. */
      /** Card theme color; the server defaults absent to 'info'. */
      severity?: 'info' | 'warning' | 'critical';
      /** When this step may reach a person. Absent or null delivers immediately,
       * day or night — which is how every step behaved before this existed.
       * Expressed as when delivery IS allowed, so a weekday window covers nights
       * and whole weekends without ranges that wrap past midnight. Outside it
       * nothing is sent; a scheduled workflow covers the quiet period. */
      deliverWindow?: BuilderDeliverWindow | null;
    }
  | {
      type: 'agent';
      name?: string;
      personaId: string;
      task: string;
      /** What the answer must cover, one point per entry — appended to the
       * task as a numbered list at run time. Absent on older definitions;
       * the server defaults it to []. */
      reportOn?: string[];
    }
  | {
      type: 'approval';
      name?: string;
      approvers: BuilderApproverRef[];
      title: string;
      onApprove: BuilderStep[];
      onDecline: BuilderStep[];
    }
  | { type: 'branch'; name?: string; cases: BuilderBranchCase[]; otherwise: BuilderStep[] }
  | {
      /** Runs a saved report as the workflow's creator and waits for the PDF. */
      type: 'report';
      name?: string;
      templateId: string;
      /** What each run is narrowed to, by name; '' for none. */
      customer: string;
      region: string;
      /** A report built for specific units: their numbers. '' otherwise. */
      units: string;
      period: string;
      /** Send the download link when it's ready; off, the PDF waits in the creator's runs. */
      sendLink: boolean;
      recipients: { kind: 'user'; id: string }[];
    }
  | {
      /** One letter per person for a bot to write up in their own thread with it. */
      type: 'bot';
      name?: string;
      botId: string;
      recipients: { kind: 'from_event'; field: string } | { kind: 'group'; groupId: string };
      /** Optional angle in the author's words; '' lets the bot decide. The bot gets the
       *  whole event and every earlier answer, and writes its own headline. */
      goal: string;
      /** Ask for a decision: every message ends in a question the people told answer. */
      ask?: boolean;
    }
  | {
      /** Calls one read tool with inputs saved at setup (watches, made from chat). */
      type: 'lookup';
      name?: string;
      tool: string;
      input: Record<string, unknown>;
    };

export interface BuilderDefinition {
  v: 1;
  createCase?: boolean;
  steps: BuilderStep[];
}

/** One row of the unified list — system entries flattened from the overview. */
export interface BuilderWorkflow {
  id: string;
  name: string;
  kind: 'system' | 'composed';
  trigger: string;
  /** Composed rows: set when the clock starts it rather than an event. */
  schedule?: BuilderSchedule | null;
  enabled: boolean;
  version: number;
  definition: BuilderDefinition | null;
  eventsLast7d: number;
  /** Composed rows: the latest LIVE run in the window, or null if it never ran. */
  lastRun?: { status: string; at: string } | null;
  /** Composed rows: unfilled slots, from the same check the enable gate runs. */
  issues?: string[];
  /** Composed rows: who a personal workflow belongs to, when it ends, and why it broke. */
  owner?: { id: string; name: string } | null;
  endsAt?: string | null;
  brokenReason?: string | null;
  /** System rows only: the catalog key, step sentences (+ branch arms), config. */
  systemKey?: string;
  systemSteps?: WorkflowStep[];
  systemBranches?: WorkflowBranch[];
  systemConfig?: unknown;
  hasConfig?: boolean;
}

export interface WorkflowRunResponse {
  caseRef?: string | null;
  id: string;
  workItemId: string;
  eventType: string;
  startedAt: string;
  completedAt: string | null;
  version: number;
  status: string;
  steps: EventRunStep[];
}

/** One Activity-page row: a run plus which workflow ran. */
export interface WorkflowActivityRow extends WorkflowRunResponse {
  /** The workflow row is gone — its builder page 404s. Optional so the UI
   *  tolerates an API deployed before the flag existed. */
  workflowDeleted?: boolean;
  workflowId: string;
  workflowName: string;
}

/** A test run: the real runner against the draft, sends rendered and held. */
export interface TestRunResponse {
  status: string;
  startedAt: string;
  completedAt: string | null;
  steps: EventRunStep[];
}

/** One step of a workflow run, resolved against the run's cursor and log. */
export interface EventRunStep {
  path: string;
  type: string;
  name: string | null;
  /** 'previewed' = a test run rendered this send in full and held it. */
  status: 'done' | 'previewed' | 'active' | 'waiting' | 'failed' | 'pending';
  detail: string | null;
  /** Raw step result — only on test-run reads, for rendering outputs/previews. */
  data?: unknown;
  at: string | null;
}

/** One workflow run triggered by an event. Carries steps that have NOT run —
 * the definition snapshot names every step, so the view can show what's next. */
export interface EventRun {
  id: string;
  workflowId: string;
  workflowName: string;
  kind: 'system' | 'composed';
  version: number;
  status: string;
  /** null = a planned run: the event is waiting; this is what will happen. */
  startedAt: string | null;
  completedAt: string | null;
  steps: EventRunStep[];
}

export interface EventRunsResponse {
  runs: EventRun[];
  /** Nothing listened to this event's type — it completed by design. */
  unrouted: boolean;
}

/** An admin role in the catalog, with this tenant's/user's state folded in. */
export interface AdminRole {
  key: string;
  displayName: string;
  description: string;
  requiresModule: string | null;
  /** False when the tenant lacks the module — the switch is disabled, not hidden. */
  available: boolean;
  /** False for roles set in the database only (Super Admin). Shown, never assignable. */
  grantableInApp: boolean;
  /** What holding this role actually unlocks, in words. */
  grants: string[];
  /** Present on user-scoped reads. */
  assigned?: boolean;
}
